import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function Rules() {
  const { adminDisplayEnabled } = useAuth();
  const isAdmin = adminDisplayEnabled;
  const [content, setContent] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/rules`);
      setContent(response.data.content || '');
      setEditingContent(response.data.content || '');
      setError(null);
    } catch (error) {
      console.error('Error fetching rules:', error);
      setError('Failed to load rules. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditingContent(content);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await axios.put(
        `${API_URL}/api/rules`,
        { content: editingContent },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setContent(editingContent);
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving rules:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('You do not have permission to edit rules.');
      } else {
        setError('Failed to save rules. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingContent(content);
    setIsEditing(false);
    setError(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Rules saved successfully!
        </Alert>
      )}

      <Paper
        sx={{
          p: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          color: 'white',
        }}
      >
        {isEditing ? (
          <Box sx={{ p: 4 }}>
            <ReactQuill
              theme="snow"
              value={editingContent}
              onChange={setEditingContent}
              style={{
                backgroundColor: 'white',
                borderRadius: 4,
                color: '#000',
              }}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  ['link'],
                  ['clean']
                ],
              }}
            />
            <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            onClick={isAdmin ? handleEdit : undefined}
            sx={{
              p: 4,
              cursor: isAdmin ? 'pointer' : 'default',
              '&:hover': isAdmin ? {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                transition: 'background-color 0.2s',
              } : {},
              '& h1, & h2, & h3': {
                color: 'white',
                marginTop: 2,
                marginBottom: 1,
              },
              '& p': {
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: 1.5,
                lineHeight: 1.6,
              },
              '& ul, & ol': {
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: 1.5,
                paddingLeft: 3,
              },
              '& li': {
                marginBottom: 0.5,
              },
              '& a': {
                color: '#8fa3f5',
                textDecoration: 'underline',
                pointerEvents: 'auto', // Allow links to be clickable
              },
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </Paper>
    </Box>
  );
}

export default Rules;

